import type { Ref } from "vue";
import type AppDataGridLookupPromise from "~/components/AppDataGridLookupPromise.vue";
import { useApiResources } from "~/stores/ApiResources";
import { FormKitAutocompleteLookupEvent } from "~/types";
import { toIndexLookupUrl } from "~utils/Forms/lookupUrl.ts";

/**
 * Opens a form field's grid lookup and writes the selection back into the field.
 *
 * Both form hosts — `FormPage` and `FormLayout` — mount their own `AppDataGridLookupPromise` and each
 * had a hand-copied version of this. They drifted: `FormLayout`'s never stripped the `/reference`
 * prefix before resolving the resource, so `findByUri` threw `Resource with uri /api/reference not
 * found` for every reference-backed field and the lookup silently never opened. Sharing the function
 * is what keeps the two hosts honest.
 */
export function useGridLookup(gridLookup: Ref<InstanceType<typeof AppDataGridLookupPromise> | undefined>) {
	const { findByUri } = useApiResources();

	return async function tryLookup(lookupEvent: FormKitAutocompleteLookupEvent) {
		try {
			// `optionSource` addresses the projection controller (`/api/reference/relations`), but the
			// resource catalogue is keyed by the resource's own uri (`/api/relations`).
			const resource = findByUri(lookupEvent.payload.originalOptionSource.replace("/reference", ""));

			const result = await gridLookup.value?.start({
				resource,
				gridKey: lookupEvent.payload.gridKey,
				selectionMode: lookupEvent.payload.selectionMode,
				customResourceUrl: toIndexLookupUrl(lookupEvent.payload.optionsSourceWithDependencies, resource),
				filters: lookupEvent.payload.filters,
			});

			if (result) {
				if (result.length === 1 && lookupEvent.payload.selectionMode === "singleRow") {
					lookupEvent.origin.input(result[0]);
				} else if (result.length >= 1 && lookupEvent.payload.selectionMode === "multiRow") {
					lookupEvent.origin.input(result);
				}
			}
		} catch (e) {
			console.error(e);
		}
	};
}
